import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
  MobileStepper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import NoteAltRounded from "@mui/icons-material/NoteAltRounded";
import ForumRounded from "@mui/icons-material/ForumRounded";
import QuestionAnswerRounded from "@mui/icons-material/QuestionAnswerRounded";
import FlashOnRounded from "@mui/icons-material/FlashOnRounded";
import MenuBookRounded from "@mui/icons-material/MenuBookRounded";
import { db } from "services/firebase";

const ToolCard = ({ icon, title, description }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      p: 2,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      textAlign: "left",
    }}
  >
    {icon}
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Box>
);

const BETA_TOOLS = [
  {
    icon: <NoteAltRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "ChartMind",
    description:
      "Record a patient encounter and let Medical SuperIntelligence suggest diagnoses, build a treatment plan, generate discharge instructions, and produce a finalized clinical note.",
  },
  {
    icon: <QuestionAnswerRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "Ask Me Anything",
    description:
      "Ask clinical questions and get answers drawn from the curated medical knowledge base.",
  },
  {
    icon: <ForumRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "PeerView Case Consultation",
    description:
      "Ask follow-up questions about your active ChartMind encounter to narrow a differential, explore alternatives, or get more clinical context.",
  },
];

const STANDARD_TOOLS = [
  {
    icon: <NoteAltRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "ChartMind",
    description:
      "Record your patient encounter and let Medical SuperIntelligence suggest diagnoses, build a treatment plan, generate discharge instructions, and produce a finalized clinical note.",
  },
  {
    icon: <ForumRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "PeerView Case Consultation",
    description:
      "Ask follow-up questions about your active ChartMind encounter to narrow a differential, explore alternatives, or get deeper clinical context.",
  },
  {
    icon: <FlashOnRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "BrainFlash",
    description:
      "Rapid clinical facts, dosing, and contraindications for time-sensitive decisions.",
  },
  {
    icon: <MenuBookRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "DeepDive",
    description:
      "Comprehensive medical reference with detailed pathophysiology and evidence.",
  },
];

const ProfessionalOnboardingDialog = ({ open, onClose, userId, userData }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const isBeta = Boolean(userData?.betaAccess);
  const tools = isBeta ? BETA_TOOLS : STANDARD_TOOLS;

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleClose = () => {
    onClose();

    if (dontShowAgain && userId) {
      db.collection("users")
        .doc(userId)
        .update({ hideOnboardingDialog: true })
        .catch((err) => console.error("Error saving onboarding preference:", err));
    }
  };

  const betaSteps = [
    {
      label: "Important Disclaimer",
      hideNavigation: true,
      content: (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <MedicalServicesIcon color="primary" sx={{ fontSize: 64, mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 3 }}>
            Important Notice for Medical Professionals
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: "center" }}>
            The materials on this site are provided for general medical education
            purposes only and should not be applied rigidly or universally in
            any clinical scenario. Decisions regarding patient care must remain
            the professional responsibility of the individual medical
            practitioner, who must use clinical judgment based on the unique
            circumstances of each case.
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: "center" }}>
            FusionMSI and its representatives assume no legal, financial, or
            medical liability for decisions made using this platform. By
            accessing this site, you agree to the full FusionMSI Terms of
            Use.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setActiveStep(1)}
            sx={{ mt: 2, px: 4 }}
          >
            I Understand
          </Button>
        </Box>
      ),
    },
    {
      label: "Welcome",
      content: (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <MedicalServicesIcon color="primary" sx={{ fontSize: 64, mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 3 }}>
            Welcome to FusionMSI
          </Typography>
          <Typography variant="body1" paragraph>
            Start with <strong>ChartMind</strong> to record your next patient
            encounter. Medical SuperIntelligence will suggest diagnoses, build a
            treatment plan, generate discharge instructions, and produce a
            finalized clinical note.
          </Typography>
          <Typography variant="body1" paragraph>
            Switch to <strong>PeerView</strong> at any point to ask follow-up
            questions about the active encounter, or jump into{" "}
            <strong>Ask Me Anything</strong> to query our curated medical
            knowledge base independently.
          </Typography>
        </Box>
      ),
    },
  ];

  const standardSteps = [
    {
      label: "Important Disclaimer",
      hideNavigation: true,
      content: (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <MedicalServicesIcon color="primary" sx={{ fontSize: 64, mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 3 }}>
            Important Notice for Medical Professionals
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: "center" }}>
            The materials on this site are provided for general medical education
            purposes only and should not be applied rigidly or universally in any
            clinical scenario. Decisions regarding patient care must remain the
            professional responsibility of the individual medical practitioner,
            who must use clinical judgment based on the unique circumstances of
            each case.
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: "center" }}>
            FusionMSI is intended strictly for use by medical professionals.
            Neither FusionMSI nor its representatives assume legal,
            financial, or medical liability for decisions made using, or based
            on, the information provided through its platform.
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: "center" }}>
            By accessing and using this site, you agree to these conditions as
            well as the full FusionMSI Terms of Use.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setActiveStep(1)}
            sx={{ mt: 2, px: 4 }}
          >
            I Understand
          </Button>
        </Box>
      ),
    },
    {
      label: "Welcome",
      content: (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <MedicalServicesIcon color="primary" sx={{ fontSize: 64, mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 3 }}>
            Welcome to FusionMSI
          </Typography>
          <Typography variant="body1" paragraph>
            FusionMSI provides AI-assisted clinical tools built on a curated
            medical knowledge base.
          </Typography>
          <Typography variant="body1" paragraph>
            Less time searching. More time with patients.
          </Typography>
        </Box>
      ),
    },
    {
      label: "Your Tools",
      content: (
        <Box sx={{ py: 2 }}>
          <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
            Your AI Assistant Suite
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {tools.map((tool) => (
              <ToolCard key={tool.title} {...tool} />
            ))}
          </Box>
          <Typography
            variant="body2"
            sx={{ mt: 3, fontStyle: "italic", color: "text.secondary", textAlign: "center" }}
          >
            Access any tool from the sidebar to get started.
          </Typography>
        </Box>
      ),
    },
    {
      label: "Get Started",
      content: (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <NoteAltRounded color="primary" sx={{ fontSize: 64, mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 3 }}>
            You're Ready to Get Started
          </Typography>
          <Typography variant="body1" paragraph>
            Open <strong>ChartMind</strong> from the sidebar to start a new
            encounter. Use <strong>BrainFlash</strong> for quick clinical facts
            or <strong>DeepDive</strong> for detailed reference material.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>PeerView</strong> is available once a ChartMind session is
            active.
          </Typography>
        </Box>
      ),
    },
  ];

  const steps = isBeta ? betaSteps : standardSteps;

  return (
    <Dialog
      open={open}
      onClose={activeStep > 0 ? handleClose : null}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      {activeStep > 0 && (
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <DialogContent sx={{ p: 4, pb: 1 }}>
        {steps[activeStep].content}

        {!steps[activeStep].hideNavigation && steps.length - 1 > 1 && (
          <MobileStepper
            variant="dots"
            steps={steps.length - 1}
            position="static"
            activeStep={activeStep - 1}
            sx={{ background: "transparent", justifyContent: "center", mt: 2 }}
            nextButton={null}
            backButton={null}
          />
        )}
      </DialogContent>

      {!steps[activeStep].hideNavigation && (
        <DialogActions sx={{ justifyContent: "space-between", p: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
            }
            label="Don't show this again"
          />
          <Box>
            {activeStep > 1 && (
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
            )}
            {activeStep === steps.length - 1 ? (
              <Button variant="contained" onClick={handleClose}>
                Get Started
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ProfessionalOnboardingDialog;
